import { ArrowRight, Briefcase } from "react-bootstrap-icons";
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import Footer from "../../components/Footer/Footer";
import SiteNav from "../../components/SiteNav/SiteNav";
import Seo from "../../components/Seo/Seo";
import { useLocale } from "../../contexts/LocaleContext";
import { createHeroParticles } from "../../utils/heroParticles";
import { navigateToHomeSection } from "../../utils/homeSectionNavigation";
import styles from "./Career.module.scss";

const VACANCIES_URL = "https://api.stafflow.by/job-vacancy-public/catlogic/vacancies";

const COPY = {
  ru: {
    kicker: "Карьера в CatLogic",
    title: "Открытые вакансии",
    body: "Строим Stafflow, кастомные ИТ-решения и AI-практику. Ищем людей, которым интересно делать продукт вместе с нами.",
    listKicker: "Вакансии",
    listTitle: "Найдите свою роль",
    count: (n: number) => `${n} ${plural(n, ["открытая позиция", "открытые позиции", "открытых позиций"])}`,
    open: "Подробнее",
    emptyTitle: "Сейчас открытых вакансий нет",
    emptyBody: "Но мы всегда рады сильным людям. Напишите нам о себе — свяжемся, как только появится подходящая роль.",
    emptyCta: "Написать нам",
    error: "Не удалось загрузить вакансии. Попробуйте обновить страницу.",
  },
  en: {
    kicker: "Careers at CatLogic",
    title: "Open positions",
    body: "We build Stafflow, custom IT solutions and an AI practice. Looking for people who want to build product with us.",
    listKicker: "Vacancies",
    listTitle: "Find your role",
    count: (n: number) => `${n} open ${n === 1 ? "position" : "positions"}`,
    open: "View",
    emptyTitle: "No open positions right now",
    emptyBody: "We're always glad to meet strong people. Tell us about yourself and we'll reach out when a matching role opens.",
    emptyCta: "Get in touch",
    error: "Couldn't load vacancies. Please refresh the page.",
  },
};

function plural(n: number, forms: [string, string, string]) {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return forms[0];
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return forms[1];
  return forms[2];
}

const capitalize = (value: string) => value.charAt(0).toUpperCase() + value.slice(1);

export default function CareerView() {
  const nav = useNavigate();
  const { locale } = useLocale();
  const copy = COPY[locale];
  const heroParticles = useMemo(() => createHeroParticles(), []);
  const [vacancies, setVacancies] = useState<IVacancy[]>();
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const findAll = async () => {
      try {
        const { data } = await axios.get<ICareer>(VACANCIES_URL);
        setVacancies(data.vacancies ?? []);
      } catch {
        setFailed(true);
      }
    };

    findAll();
  }, []);

  return (
    <main className={styles.page}>
      <Seo
        title="CatLogic | Карьера"
        description="Открытые вакансии CatLogic: присоединяйтесь к команде продуктовой студии из Минска, которая строит Stafflow, кастомные ИТ-решения и AI-практику."
        keywords="CatLogic вакансии, карьера CatLogic, работа Минск IT, вакансии разработчик, вакансии дизайнер"
        path="/career"
      />
      <SiteNav solidThreshold={320} />

      <section className={styles.hero}>
        <div className={styles.heroParticles} aria-hidden="true">
          {heroParticles.map((particle, index) => (
            <span
              key={index}
              className={styles.particle}
              style={
                {
                  width: particle.size,
                  height: particle.size,
                  top: particle.top,
                  left: particle.left,
                  background: `radial-gradient(circle, ${particle.color}, transparent 70%)`,
                  animationDuration: particle.duration,
                  animationDelay: particle.delay,
                  "--particle-scale-mid": particle.scaleMid,
                  "--particle-scale-low": particle.scaleLow,
                } as React.CSSProperties
              }
            />
          ))}
        </div>

        <div className={styles.container}>
          <div className={styles.heroCenter}>
            <span className={styles.heroCaption}>{copy.kicker}</span>
            <h1 className={styles.heroTitle}>{copy.title}</h1>
            <p className={styles.heroDescription}>{copy.body}</p>
            {vacancies && vacancies.length > 0 && <span className={styles.heroCount}>{copy.count(vacancies.length)}</span>}
          </div>
        </div>
      </section>

      <div className={styles.surface}>
        <section className={styles.lightBand}>
          <div className={styles.container}>
            <div className={styles.sectionHeading}>
              <span className={styles.kicker}>{copy.listKicker}</span>
              <h2>{copy.listTitle}</h2>
            </div>

            {failed ? (
              <p className={styles.state}>{copy.error}</p>
            ) : !vacancies ? (
              <div className={styles.grid} aria-busy="true">
                {Array.from({ length: 6 }, (_, index) => (
                  <div key={index} className={styles.skeleton}>
                    <span />
                    <span />
                  </div>
                ))}
              </div>
            ) : vacancies.length === 0 ? (
              <div className={styles.placeholder}>
                <span className={styles.placeholderIcon} aria-hidden="true">
                  <Briefcase />
                </span>
                <h3>{copy.emptyTitle}</h3>
                <p>{copy.emptyBody}</p>
                <button type="button" className={styles.primaryButton} onClick={() => navigateToHomeSection(nav, "contacts")}>
                  {copy.emptyCta}
                  <ArrowRight />
                </button>
              </div>
            ) : (
              <div className={styles.grid}>
                {vacancies.map((vacancy) => {
                  const tags = vacancy.subtitle
                    .split("•")
                    .map((tag) => tag.trim())
                    .filter(Boolean);

                  return (
                    <Link key={vacancy.id} className={styles.card} to={`/career/${encodeURIComponent(vacancy.id)}`}>
                      <h3>{capitalize(vacancy.name)}</h3>
                      {tags.length > 0 && (
                        <ul className={styles.tags}>
                          {tags.map((tag) => (
                            <li key={tag}>{tag}</li>
                          ))}
                        </ul>
                      )}
                      <span className={styles.cardLink}>
                        {copy.open}
                        <ArrowRight />
                      </span>
                    </Link>
                  );
                })}
              </div>
            )}
          </div>
        </section>
      </div>

      <Footer />
    </main>
  );
}

interface ICareer {
  vacancies: IVacancy[];
}

interface IVacancy {
  id: string; // "eyJ0ZW5hbnQiOiJjYXRsb2dpYyIsImlkIjoiOGQ0ODkxMjctODg2MC00OWVmLTk4ZTctZWE5ODE1YmU2MzZhIn0=";
  url: string; // "https://app.stafflow.by/careers/alex/eyJ0ZW5h...";
  name: string; //  "Менеджер по продажам В2В (лидоруб)";
  subtitle: string; //   "Менеджер по продажам, менеджер по работе с клиентами • Ждановичи";
}
